import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeftIcon, EnvelopeIcon, PlusIcon, PlayIcon } from "@heroicons/react/24/outline";
import { PageLoader, TabNavigation, Button } from "../../ui";
import { FormModal, FormModalHeader, FormModalBody, FormModalField, FormModalFooter, formInputCls } from "../../ui/FormModal";
import VaptStatusBadge from "../components/VaptStatusBadge";
import VaptSeverityBadge from "../components/VaptSeverityBadge";
import VaptTable from "../components/VaptTable";
import VaptNotificationPreviewModal from "../components/VaptNotificationPreviewModal";
import { useAuth } from "../../../context/AuthContext";
import {
  getVaptProjectApi,
  listVaptScopeApi,
  listVaptTargetsApi,
  listVaptScansApi,
  listVaptFindingsApi,
  listVaptRemediationApi,
  listVaptReportRecordsApi,
  downloadVaptReportApi,
  regenerateVaptReportApi,
  getVaptAssetManagerAssetsApi,
  createVaptScopeApi,
  createVaptTargetApi,
  createVaptAuthorizationApi,
  listVaptAuthorizationsApi,
  approveVaptAuthorizationApi,
  getVaptExecutionCoverageApi,
  listVaptExceptionsApi,
  createVaptExceptionApi,
  decideVaptExceptionApi,
} from "../../../services/axios/AuthApis/VAPTApis";
import { VAPT_DOMAINS } from "../utils/vaptConstants";
import { validateTargetForDomain } from "../utils/targetValidation";
import { getVaptErrorMessage } from "../utils/apiError";

const PLATFORM_ADMIN_ROLES = ["platform_admin", "admin"];
// Roles allowed to approve a Rules-of-Engagement authorization (mirrors
// AuthorizationViewSet._AUTH_DECISION_ROLES on the backend). customer_admin
// may approve only their own tenant's authorizations - the backend enforces
// that boundary; the frontend only uses this to decide which control to show.
const AUTH_APPROVER_ROLES = ["platform_admin", "admin", "customer_admin"];

const asArray = (data) => (Array.isArray(data) ? data : data?.results || []);

const emptyTargetForm = { target_type: "Web", target_value: "", scope: "In Scope", authorization: "Not Confirmed" };

const VaptProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [scope, setScope] = useState([]);
  const [targets, setTargets] = useState([]);
  const [scans, setScans] = useState([]);
  const [findings, setFindings] = useState([]);
  const [remediation, setRemediation] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("overview");
  const [showNotificationPreview, setShowNotificationPreview] = useState(false);
  const [notice, setNotice] = useState("");
  const [showAddTarget, setShowAddTarget] = useState(false);
  const [targetForm, setTargetForm] = useState(emptyTargetForm);
  const [savingTarget, setSavingTarget] = useState(false);
  const [targetError, setTargetError] = useState(null);
  // Live Asset Manager summary for this project's customer — only fetched
  // (and only shown) when this is an engagement_type="asset" project, per
  // section 4's Asset Summary requirement. Sourced from the same live,
  // non-persisting endpoint the Projects page card/inventory modal use, not
  // fabricated or re-derived from VAPTProject/VAPTTarget rows.
  const [assetSummary, setAssetSummary] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [exceptions, setExceptions] = useState([]);
  const [authorizations, setAuthorizations] = useState([]);
  const [approvingAuthId, setApprovingAuthId] = useState(null);
  const [showAddException, setShowAddException] = useState(false);
  const [exceptionForm, setExceptionForm] = useState({ target: "", reason: "" });
  const [savingException, setSavingException] = useState(false);
  const isPlatformAdmin = PLATFORM_ADMIN_ROLES.includes(user?.role);
  const canApproveAuthorization = AUTH_APPROVER_ROLES.includes(user?.role);

  const load = () => {
    let mounted = true;
    setLoading(true);
    setError(null);
    Promise.all([
      getVaptProjectApi(projectId),
      listVaptScopeApi(projectId),
      listVaptTargetsApi({ project_id: projectId }),
      listVaptScansApi({ project_id: projectId }),
      listVaptFindingsApi({ project_id: projectId }),
      listVaptRemediationApi({ project_id: projectId }),
      listVaptReportRecordsApi(projectId),
      getVaptExecutionCoverageApi(projectId).catch(() => null),
      listVaptExceptionsApi({ project_id: projectId }).catch(() => []),
      listVaptAuthorizationsApi(projectId).catch(() => []),
    ])
      .then(([proj, sc, tg, sn, fn, rem, rep, cov, exc, auth]) => {
        if (!mounted) return;
        setProject(proj);
        setScope(asArray(sc));
        setTargets(asArray(tg));
        setAuthorizations(asArray(auth));
        setScans(asArray(sn).map((s) => ({ ...s, id: s.scan_id })));
        setFindings(asArray(fn).map((f) => ({ ...f, id: f.finding_id })));
        setRemediation(asArray(rem).map((r) => ({ ...r, id: r.remediation_id || r.id })));
        setReports(asArray(rep).map((r) => ({ ...r, id: r.report_id || r.id })));
        setCoverage(cov);
        setExceptions(asArray(exc));
        setLoading(false);
        if (proj?.engagement_type === "asset" && proj?.customer_id) {
          getVaptAssetManagerAssetsApi(proj.customer_id)
            .then((res) => {
              if (mounted) setAssetSummary(res?.summary || null);
            })
            .catch(() => {
              if (mounted) setAssetSummary(null);
            });
        }
      })
      .catch((err) => {
        if (!mounted) return;
        setError(getVaptErrorMessage(err, "Failed to load project."));
        setLoading(false);
      });
    return () => {
      mounted = false;
    };
  };

  useEffect(load, [projectId]);

  const openAddTarget = () => {
    setTargetForm(emptyTargetForm);
    setTargetError(null);
    setShowAddTarget(true);
  };

  const submitAddTarget = async (e) => {
    e.preventDefault();
    // Same rules as the backend (utils/targetValidation.js mirrors
    // vapt/target_validation.py) so the user gets immediate feedback; the
    // backend remains authoritative and rejects before anything is saved.
    const validationError = validateTargetForDomain(targetForm.target_value, targetForm.target_type);
    if (validationError) {
      setTargetError(validationError);
      return;
    }
    setSavingTarget(true);
    setTargetError(null);
    try {
      const inScope = targetForm.scope === "In Scope";
      const requestApproval = targetForm.authorization === "Confirmed";
      // A new target is NEVER created pre-authorized. An in-scope target
      // starts at "Pending Authorization" and only becomes "Authorized"
      // when its Authorization document is actually approved through the
      // dedicated approve endpoint (see approveTargetAuthorization / the
      // backend AuthorizationViewSet.approve, which also transitions the
      // target). This keeps target.status honest with the real
      // authorization lifecycle and matches the security gate.
      const status = !inScope ? "Out of Scope" : "Pending Authorization";

      const newTarget = await createVaptTargetApi({
        project: project.id,
        source: "external",
        external_value: targetForm.target_value.trim(),
        target_type: targetForm.target_type,
        domain: targetForm.target_type,
        status,
      });

      await createVaptScopeApi({
        project: project.id,
        in_scope: inScope ? [targetForm.target_value.trim()] : [],
        out_of_scope: inScope ? [] : [targetForm.target_value.trim()],
      });

      // Every in-scope external target gets a Rules-of-Engagement
      // authorization record, created "Pending" (the backend ignores any
      // status sent here - approval is a separate, permission-checked
      // action). If the person adding the target both asked for it to be
      // authorized AND holds an approver role, approve it immediately in
      // the same flow; otherwise it correctly stays Pending for a reviewer.
      if (inScope && newTarget?.id) {
        const newAuth = await createVaptAuthorizationApi({
          project: project.id,
          target: newTarget.id,
        });
        if (requestApproval && canApproveAuthorization && newAuth?.authorization_id) {
          try {
            await approveVaptAuthorizationApi(newAuth.authorization_id);
          } catch (approveErr) {
            setTargetError(
              getVaptErrorMessage(
                approveErr,
                "Target added, but the authorization could not be approved automatically — it remains pending.",
              ),
            );
          }
        }
      }

      setShowAddTarget(false);
      load();
    } catch (err) {
      setTargetError(getVaptErrorMessage(err, "Failed to add target."));
    } finally {
      setSavingTarget(false);
    }
  };

  const approveTargetAuthorization = async (authorizationId) => {
    setApprovingAuthId(authorizationId);
    setNotice("");
    try {
      await approveVaptAuthorizationApi(authorizationId);
      load();
    } catch (err) {
      setNotice(getVaptErrorMessage(err, "Failed to approve the authorization."));
    } finally {
      setApprovingAuthId(null);
    }
  };

  const submitException = async (e) => {
    e.preventDefault();
    if (!exceptionForm.target || !exceptionForm.reason.trim()) return;
    setSavingException(true);
    try {
      await createVaptExceptionApi({
        target: exceptionForm.target,
        reason: exceptionForm.reason.trim(),
      });
      setShowAddException(false);
      setExceptionForm({ target: "", reason: "" });
      load();
    } catch (err) {
      setNotice(getVaptErrorMessage(err, "Failed to request exception."));
    } finally {
      setSavingException(false);
    }
  };

  const decideException = async (exceptionId, decision) => {
    try {
      await decideVaptExceptionApi(exceptionId, decision);
      load();
    } catch {
      setNotice("Failed to decide on this exception.");
    }
  };

  if (loading) return <PageLoader title="Loading project…" />;
  if (error) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-500">{error}</p>
      </div>
    );
  }
  if (!project) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-500">Project not found.</p>
      </div>
    );
  }

  const inScopeTargets = targets.filter((t) => t.status !== "Out of Scope");
  const outOfScopeTargets = targets.filter((t) => t.status === "Out of Scope");

  // Real authorization document for a target (not the target's own status
  // string). `auth.target` is the target's Mongo id, the same value as
  // `t.id`. The most recently created record wins if there is more than one.
  const refId = (v) => (v && typeof v === "object" ? v.id || v._id || v.$oid || "" : v ?? "");
  const authForTarget = (targetRow) => {
    const tid = String(targetRow.id);
    const matches = authorizations.filter((a) => String(refId(a.target)) === tid);
    return matches[matches.length - 1] || null;
  };
  const isAuthExpired = (a) =>
    !!a?.valid_until && new Date(a.valid_until).getTime() < Date.now();
  // Display state derived strictly from the Authorization document.
  const authDisplay = (a) => {
    if (!a) return { label: "Not Requested", cls: "text-gray-400", note: "No authorization on file." };
    if (a.status === "Approved" && isAuthExpired(a))
      return { label: "Expired", cls: "text-red-600", note: "Authorization expired." };
    switch (a.status) {
      case "Approved":
        return { label: "Approved", cls: "text-green-700", note: "Authorization approved." };
      case "Rejected":
        return { label: "Rejected", cls: "text-red-600", note: "Authorization rejected." };
      case "Expired":
        return { label: "Expired", cls: "text-red-600", note: "Authorization expired." };
      case "Revoked":
        return { label: "Revoked", cls: "text-red-600", note: "Authorization revoked." };
      default:
        return { label: "Pending", cls: "text-amber-600", note: "Awaiting authorization approval." };
    }
  };

  const tabs = [
    { id: "overview", name: "Overview" },
    { id: "scope", name: "Scope & Targets" },
    { id: "scans", name: "Scans" },
    { id: "findings", name: "Findings" },
    { id: "risk", name: "Risk" },
    { id: "remediation", name: "Remediation" },
    { id: "reports", name: "Reports" },
  ];

  const targetColumns = [
    { key: "target_type", header: "Type", render: (r) => r.target_type ?? "—" },
    { key: "value", header: "Target", render: (r) => r.asset_display_name || r.external_value || "—" },
    {
      key: "authorization",
      header: "Authorization",
      render: (r) => {
        const a = authForTarget(r);
        const d = authDisplay(a);
        const showApprove =
          a && a.status === "Pending" && canApproveAuthorization;
        return (
          <div className="flex items-center gap-2">
            <span className={`text-xs font-semibold ${d.cls}`} title={d.note}>
              {d.label}
            </span>
            {showApprove && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  approveTargetAuthorization(a.authorization_id);
                }}
                disabled={approvingAuthId === a.authorization_id}
                className="text-xs font-semibold text-primary-600 hover:text-primary-700 underline disabled:opacity-50"
              >
                {approvingAuthId === a.authorization_id ? "Approving…" : "Approve Authorization"}
              </button>
            )}
          </div>
        );
      },
    },
    { key: "status", header: "Status", render: (r) => <VaptStatusBadge status={r.status} size="sm" /> },
  ];

  return (
    <div className="space-y-6">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
      >
        <ArrowLeftIcon className="w-4 h-4" /> Back to Projects
      </button>

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h2 className="text-xl font-bold text-gray-900">{project.name}</h2>
            <VaptStatusBadge status={project.status} />
          </div>
          <p className="text-sm text-gray-500">
            {project.client_name || "—"}
            {project.engagement_type && (
              <>
                {" "}
                • <span className="font-semibold text-gray-700">
                  VAPT Type: {project.engagement_type === "asset" ? "Asset VAPT" : "External VAPT"}
                </span>
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            icon={PlayIcon}
            onClick={() => navigate(`/ai-vapt/scans/new?project=${project.project_id}`)}
          >
            New Scan
          </Button>
          <Button variant="secondary" icon={EnvelopeIcon} onClick={() => setShowNotificationPreview(true)}>
            Customer Notification Preview
          </Button>
        </div>
      </div>

      {notice && (
        <div className="bg-amber-50 border border-amber-200 text-amber-700 text-sm rounded-lg px-4 py-2.5">{notice}</div>
      )}

      <TabNavigation tabs={tabs} activeTab={tab} onTabChange={setTab} boxedActive />

      {tab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl p-5 space-y-3">
            <p className="text-sm text-gray-700">{project.description || "—"}</p>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs text-gray-400 uppercase">Team Members</p>
                <p className="text-gray-800">
                  {Array.isArray(project.team_members) && project.team_members.length > 0
                    ? project.team_members.join(", ")
                    : "—"}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase">VAPT Type</p>
                <p className="text-gray-800">
                  {project.engagement_type ? (project.engagement_type === "asset" ? "Asset VAPT" : "External VAPT") : "—"}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase">Project Type</p>
                <p className="text-gray-800">{project.project_type || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase">Start Date</p>
                <p className="text-gray-800">{project.start_date || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase">End Date</p>
                <p className="text-gray-800">{project.end_date || "—"}</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Targets</span>
              <span className="font-bold text-gray-800">{targets.length}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Scans</span>
              <span className="font-bold text-gray-800">{scans.length}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Findings</span>
              <span className="font-bold text-gray-800">{findings.length}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Remediation</span>
              <span className="font-bold text-gray-800">{remediation.length}</span>
            </div>
          </div>

          {project.engagement_type === "asset" && (
            <div className="lg:col-span-3 bg-white border border-gray-200 rounded-xl p-5 space-y-4">
              <div>
                <p className="text-xs text-gray-400 uppercase mb-2">Asset Summary — Asset Manager (Live)</p>
                {assetSummary ? (
                  <div className="grid grid-cols-3 gap-4 text-center max-w-md">
                    <div>
                      <p className="text-lg font-bold text-gray-800">{assetSummary.total ?? 0}</p>
                      <p className="text-[10px] text-gray-400 uppercase">Total</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-green-700">{assetSummary.eligible ?? 0}</p>
                      <p className="text-[10px] text-gray-400 uppercase">Applicable</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-amber-600">{assetSummary.unsupported ?? 0}</p>
                      <p className="text-[10px] text-gray-400 uppercase">Unsupported</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">Asset Manager summary unavailable for this project's customer.</p>
                )}
              </div>

              <div>
                <p className="text-xs text-gray-400 uppercase mb-2">VAPT Execution</p>
                {scans.length === 0 && (
                  <p className="text-sm text-gray-500 mb-2">No VAPT scan has been executed yet.</p>
                )}
                <div className="grid grid-cols-3 gap-4 text-center max-w-md">
                  <div>
                    <p className="text-lg font-bold text-gray-800">{scans.length}</p>
                    <p className="text-[10px] text-gray-400 uppercase">Scans</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-green-700">
                      {scans.filter((s) => s.status === "Completed").length}
                    </p>
                    <p className="text-[10px] text-gray-400 uppercase">Completed</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-red-600">
                      {scans.filter((s) => s.status === "Failed").length}
                    </p>
                    <p className="text-[10px] text-gray-400 uppercase">Failed</p>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-xs text-gray-400 uppercase mb-2">Findings</p>
                <div className="grid grid-cols-4 gap-4 text-center max-w-lg">
                  {["Critical", "High", "Medium", "Low"].map((sev) => (
                    <div key={sev}>
                      <p className="text-lg font-bold text-gray-800">
                        {findings.filter((f) => f.severity === sev).length}
                      </p>
                      <p className="text-[10px] text-gray-400 uppercase">{sev}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {coverage && (
            <div className="lg:col-span-3 bg-white border border-gray-200 rounded-xl p-5">
              <p className="text-xs text-gray-400 uppercase mb-2">
                Coverage — computed from targets and tool executions
              </p>
              <div className="grid grid-cols-5 gap-4 text-center max-w-lg">
                {[
                  ["Applicable", coverage.summary?.applicable, "text-gray-800"],
                  ["Implemented", coverage.summary?.implemented, "text-green-700"],
                  ["Not Implemented", coverage.summary?.not_implemented, "text-amber-600"],
                  ["Not Applicable", coverage.summary?.not_applicable, "text-gray-400"],
                  ["Unsupported", coverage.summary?.unsupported, "text-red-600"],
                ].map(([label, value, cls]) => (
                  <div key={label}>
                    <p className={`text-lg font-bold ${cls}`}>{value ?? 0}</p>
                    <p className="text-[10px] text-gray-400 uppercase">{label}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "scope" && (
        <div className="space-y-6">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-800">In-Scope Targets</h3>
              <Button size="sm" icon={PlusIcon} onClick={openAddTarget}>
                Add Target
              </Button>
            </div>
            <p className="text-xs text-gray-400">
              Only these targets, once their authorization is Approved, can ever be passed to a scan's checkers/tools.
            </p>
            <VaptTable
              columns={targetColumns}
              rows={inScopeTargets.map((t) => ({ ...t, id: t.id }))}
              emptyMessage="No in-scope targets yet. Add one above."
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-800">Out-of-Scope Targets</h3>
              <Button size="sm" variant="secondary" icon={PlusIcon} onClick={openAddTarget}>
                Add Target
              </Button>
            </div>
            <p className="text-xs text-gray-400">
              These are explicitly excluded — the scanner will never run against them, regardless of authorization.
            </p>
            <VaptTable
              columns={targetColumns}
              rows={outOfScopeTargets.map((t) => ({ ...t, id: t.id }))}
              emptyMessage="No out-of-scope targets recorded."
            />
          </div>

          {scope.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-bold text-gray-800">Scope Records</h3>
              <VaptTable
                columns={[
                  { key: "in_scope", header: "In Scope", render: (r) => (r.in_scope || []).join(", ") || "—" },
                  { key: "out_of_scope", header: "Out of Scope", render: (r) => (r.out_of_scope || []).join(", ") || "—" },
                  { key: "environment", header: "Environment", render: (r) => r.environment ?? "—" },
                ]}
                rows={scope.map((s, i) => ({ ...s, id: s.id ?? i }))}
                emptyMessage="No scope records for this project."
              />
            </div>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-800">Scope Exceptions</h3>
              <Button
                size="sm"
                variant="secondary"
                icon={PlusIcon}
                onClick={() => {
                  setExceptionForm({ target: targets[0]?.id || "", reason: "" });
                  setShowAddException(true);
                }}
                disabled={targets.length === 0}
              >
                Request Exception
              </Button>
            </div>
            <p className="text-xs text-gray-400">
              A deliberate, reviewed carve-out for an unsupported/restricted target — never overrides authorization,
              and does not by itself make a target scannable.
            </p>
            <VaptTable
              columns={[
                { key: "target_display_name", header: "Target", render: (r) => r.target_display_name || "—" },
                { key: "reason", header: "Reason" },
                { key: "requested_by", header: "Requested By" },
                { key: "status", header: "Status", render: (r) => <VaptStatusBadge status={r.status} size="sm" /> },
                {
                  key: "actions",
                  header: "Actions",
                  render: (r) =>
                    isPlatformAdmin && r.status === "Pending" ? (
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={(e) => { e.stopPropagation(); decideException(r.exception_id, "Approved"); }} className="text-xs font-semibold text-green-700 hover:text-green-800 underline">
                          Approve
                        </button>
                        <button type="button" onClick={(e) => { e.stopPropagation(); decideException(r.exception_id, "Rejected"); }} className="text-xs font-semibold text-red-600 hover:text-red-700 underline">
                          Reject
                        </button>
                      </div>
                    ) : (
                      "—"
                    ),
                },
              ]}
              rows={exceptions.map((e) => ({ ...e, id: e.exception_id }))}
              emptyMessage="No scope exceptions requested."
            />
          </div>
        </div>
      )}

      {tab === "scans" && (
        <VaptTable
          columns={[
            { key: "scan_id", header: "Scan ID" },
            { key: "scan_name", header: "Scan Name", render: (r) => r.scan_name || "—" },
            { key: "scan_type", header: "Type", render: (r) => r.scan_type ?? "—" },
            { key: "status", header: "Status", render: (r) => <VaptStatusBadge status={r.status} size="sm" /> },
            { key: "vulnerabilities_found", header: "Findings", render: (r) => r.vulnerabilities_found ?? "—" },
          ]}
          rows={scans}
          onRowClick={(row) => navigate(`/ai-vapt/scans/${row.scan_id}`)}
          emptyMessage="No scans yet for this project."
        />
      )}

      {tab === "findings" && (
        <VaptTable
          columns={[
            { key: "title", header: "Finding" },
            { key: "severity", header: "Severity", render: (r) => <VaptSeverityBadge severity={r.severity} size="sm" /> },
            { key: "target_display", header: "Target", render: (r) => r.target_display ?? "—" },
            {
              key: "mode",
              header: "Source",
              render: (r) => (
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${r.execution_mode === "Demo" ? "bg-amber-50 text-amber-700" : "bg-green-50 text-green-700"}`}>
                  {r.execution_mode === "Demo" ? "Simulation" : r.execution_mode === "Real" ? "Executed" : "—"}
                </span>
              ),
            },
            { key: "validation_status", header: "Validation", render: (r) => <VaptStatusBadge status={r.validation_status} size="sm" /> },
            { key: "finding_status", header: "Status", render: (r) => <VaptStatusBadge status={r.finding_status} size="sm" /> },
          ]}
          rows={findings}
          onRowClick={(row) => navigate(`/ai-vapt/findings/${row.finding_id}`)}
          emptyMessage="No findings yet for this project."
        />
      )}

      {tab === "risk" && (
        <VaptTable
          columns={[
            { key: "title", header: "Finding" },
            { key: "severity", header: "Severity", render: (r) => <VaptSeverityBadge severity={r.severity} size="sm" /> },
            { key: "finding_status", header: "Status", render: (r) => <VaptStatusBadge status={r.finding_status} size="sm" /> },
          ]}
          rows={findings}
          emptyMessage="No findings yet for this project — Risk is calculated from Findings."
        />
      )}

      {tab === "remediation" && (
        <VaptTable
          columns={[
            { key: "finding_title", header: "Finding", render: (r) => r.finding_title || "—" },
            { key: "recommendation", header: "Recommendation", render: (r) => r.recommendation || "—" },
            { key: "priority", header: "Priority", render: (r) => <VaptSeverityBadge severity={r.priority} size="sm" /> },
            { key: "status", header: "Status", render: (r) => <VaptStatusBadge status={r.status} size="sm" /> },
            { key: "owner", header: "Owner", render: (r) => r.owner || r.assigned_to || "—" },
          ]}
          rows={remediation}
          emptyMessage="No remediation records yet — they're created automatically from findings."
        />
      )}

      {tab === "reports" && (
        <VaptTable
          columns={[
            { key: "report_id", header: "Report" },
            { key: "report_type", header: "Type", render: (r) => r.report_type ?? "—" },
            { key: "file_format", header: "Format", render: (r) => r.file_format ?? "—" },
            { key: "status", header: "Status", render: (r) => <VaptStatusBadge status={r.status} size="sm" /> },
            {
              key: "actions",
              header: "Actions",
              // Only ever offered once the backend confirms a real file
              // exists (status === "Generated") - never a fake download
              // link for a Pending/Failed report record.
              render: (r) =>
                r.status === "Generated" && r.file_available !== false ? (
                  <button
                    type="button"
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        const blob = await downloadVaptReportApi(r.report_id);
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `${r.report_id}.${(r.file_format || "html").toLowerCase()}`;
                        a.click();
                        window.URL.revokeObjectURL(url);
                      } catch (err) {
                        setNotice(getVaptErrorMessage(err, "Failed to download this report."));
                      }
                    }}
                    className="text-xs font-semibold text-primary-600 hover:text-primary-700 underline"
                  >
                    Download
                  </button>
                ) : (
                  <span className="flex items-center gap-2 text-xs text-gray-500">
                    {r.status === "Pending" ? "Generating…" : "Generation failed"}
                    {r.status !== "Pending" && (
                      <button
                        type="button"
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            await regenerateVaptReportApi(r.report_id);
                            setNotice(`Report ${r.report_id} regenerated.`);
                            load();
                          } catch (err) {
                            setNotice(getVaptErrorMessage(err, "Failed to regenerate this report."));
                          }
                        }}
                        className="font-semibold text-primary-600 hover:text-primary-700 underline"
                      >
                        Regenerate
                      </button>
                    )}
                  </span>
                ),
            },
          ]}
          rows={reports}
          emptyMessage="No reports have been generated for this engagement."
        />
      )}

      {showNotificationPreview && (
        <VaptNotificationPreviewModal
          projectId={projectId}
          onClose={() => setShowNotificationPreview(false)}
          onPreviewOnly={() => setNotice("Preview only — no email was sent.")}
        />
      )}

      <FormModal open={showAddTarget} onClose={() => setShowAddTarget(false)} maxWidth="md">
        <FormModalHeader
          title="Add Target"
          subtitle="Manually-authorized target for this External VAPT project — no Asset Manager sync required."
          onClose={() => setShowAddTarget(false)}
        />
        <FormModalBody id="vapt-add-target-form" onSubmit={submitAddTarget}>
          {targetError && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-2.5 mb-3">{targetError}</div>
          )}
          <FormModalField label="Target Type" required>
            <select
              className={formInputCls(false)}
              value={targetForm.target_type}
              onChange={(e) => setTargetForm({ ...targetForm, target_type: e.target.value })}
              required
            >
              {VAPT_DOMAINS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </FormModalField>
          <FormModalField label="Target Value" required>
            <input
              className={formInputCls(false)}
              placeholder="https://www.demo.com"
              value={targetForm.target_value}
              onChange={(e) => setTargetForm({ ...targetForm, target_value: e.target.value })}
              required
            />
          </FormModalField>
          <div className="grid grid-cols-2 gap-4">
            <FormModalField label="Scope" required>
              <select
                className={formInputCls(false)}
                value={targetForm.scope}
                onChange={(e) => setTargetForm({ ...targetForm, scope: e.target.value })}
              >
                <option value="In Scope">In Scope</option>
                <option value="Out of Scope">Out of Scope</option>
              </select>
            </FormModalField>
            <FormModalField label="Authorization" required>
              <select
                className={formInputCls(false)}
                value={targetForm.authorization}
                onChange={(e) => setTargetForm({ ...targetForm, authorization: e.target.value })}
              >
                <option value="Not Confirmed">Request approval later</option>
                <option value="Confirmed">
                  {canApproveAuthorization ? "Approve now" : "Confirm authorization"}
                </option>
              </select>
            </FormModalField>
          </div>
          {targetForm.scope === "Out of Scope" && (
            <p className="text-xs text-amber-600">
              Out-of-scope targets are recorded for reference only — the scanner will never run against them.
            </p>
          )}
          {targetForm.scope === "In Scope" && (
            <p className="text-xs text-amber-600">
              {targetForm.authorization === "Confirmed" && canApproveAuthorization
                ? "The authorization will be created and approved immediately. The target becomes scannable only after approval."
                : "A Pending authorization will be created. The target is not scannable until an authorized reviewer approves it."}
            </p>
          )}
        </FormModalBody>
        <FormModalFooter
          formId="vapt-add-target-form"
          onCancel={() => setShowAddTarget(false)}
          saving={savingTarget}
          submitLabel="Target"
        />
      </FormModal>

      <FormModal open={showAddException} onClose={() => setShowAddException(false)} maxWidth="md">
        <FormModalHeader
          title="Request Scope Exception"
          subtitle="Requires Platform Admin approval — never bypasses authorization or scope validation on its own."
          onClose={() => setShowAddException(false)}
        />
        <FormModalBody id="vapt-add-exception-form" onSubmit={submitException}>
          <FormModalField label="Target" required>
            <select
              className={formInputCls(false)}
              value={exceptionForm.target}
              onChange={(e) => setExceptionForm({ ...exceptionForm, target: e.target.value })}
              required
            >
              {targets.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.asset_display_name || t.external_value || t.target_type || t.id}
                </option>
              ))}
            </select>
          </FormModalField>
          <FormModalField label="Reason" required>
            <textarea
              className={formInputCls(false)}
              rows={3}
              value={exceptionForm.reason}
              onChange={(e) => setExceptionForm({ ...exceptionForm, reason: e.target.value })}
              required
            />
          </FormModalField>
        </FormModalBody>
        <FormModalFooter formId="vapt-add-exception-form" onCancel={() => setShowAddException(false)} saving={savingException} submitLabel="Exception" />
      </FormModal>
    </div>
  );
};

export default VaptProjectDetail;
